import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router';

import { datadogRum } from '@datadog/browser-rum';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/react';
import { DragDropProvider, DragOverlay } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { IconEdit, IconPlus, IconTrash, IconWorld } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useAmplitudeTrack } from 'amplitude/useAmplitude';
import { useAtom, useAtomValue } from 'jotai';
import isEqual from 'lodash/isEqual';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';
import {
  createQuiz as createQuizRequest,
  deleteQuiz as deleteQuizRequest,
  getQuizDetailsOptions,
  getQuizDetailsQueryKey,
  getQuizzesQueryKey,
  patchQuizName,
  QuizCreateRequest,
  QuizDetail,
  updateQuiz as updateQuizRequest,
} from '@cfa/pathway-fe-shared/xp';
import {
  Button,
  Card,
  CardContent,
  Dropdown,
  useBreakpoints,
  useMediaQuery,
} from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { withRoles } from '@/utils/withRoles';

import StickyMenuButton from '@/components/Cards/StickyMenuCard/StickyMenuButton';
import StickyMenuCard from '@/components/Cards/StickyMenuCard/StickyMenuCard';
import { Header } from '@/components/header';
import type { ManageTranslationsState } from '@/components/popups/ManageTranslationsModal/state';

import { queryClient } from '@/services/api';
import type { CustomError } from '@/types/types';

import GenericError from 'components/Error/GenericError';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay';
import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import ConfirmationModal from 'components/popups/ConfirmationModal';
import ManageTranslationsModal from 'components/popups/ManageTranslationsModal/ManageTranslationsModal';
import ToastMessageBlock from 'components/Toasts/SuccessToast';
import Constants, { routes } from 'constants/index';
import { getNameFromLanguage, itemByLanguage } from 'utils/language';
import { isApiError } from 'utils/request';
import { cn } from 'utils/tailwind';
import { validateTextField } from 'utils/validateTextField';

import CollapsedQuestionPreview from './BuildQuizzesItems/CollapsedQuestionPreview';
import DeleteQuizPopup from './BuildQuizzesItems/DeleteQuizPopup';
import RenameQuizPopup from './BuildQuizzesItems/RenameQuizPopup';
import SortableQuestionCard from './BuildQuizzesItems/SortableQuestionCard';
import ValidationErrorPopup from './BuildQuizzesItems/ValidationErrorPopup';
import { patchQuizDetailsQueryCache } from './patchQuizDetailsQueryCache';
import {
  collectServerQuestionIdentifiers,
  emptyServerQuestionIdentifiers,
  prepareQuestionsForSave,
  resolveServerQuestionIdentifiers,
} from './prepareQuizQuestionsForSave';
import {
  quizDetailToUpdateBody,
  toQuizUpdateRequest,
} from './quizDetailToUpdateBody';
import type {
  EditableQuizDetail,
  QuizEditorQuestions,
} from './quizEditorTypes';

import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';
import {
  buildQuizWallClockDurationProps,
  clearMatchingQuizDraft,
  getPendingSaveFlagSync,
  type QuizDraft,
  quizDraftAtom,
  setPendingSaveFlagSync,
  shouldRestoreQuizDraft,
} from '@/atoms/quiz-draft';

interface BuildQuizzesLocationState {
  operator?: {
    id?: string;
  };
  quizName?: string;
  buildStartedAt?: number;
}

/** Sentinel `:quizId` for `/plans/manage-quizzes/new` (shared create+edit route). */
const CREATE_ROUTE_QUIZ_ID = 'new';

// Drag-and-drop animation timing
//
// These values are interdependent — change them together:
//
//   SCROLL_TRIGGER_DELAY_MS delay after drop before beginning the two-phase
//                           scroll sequence (original position → new position).
//                           Must be >= the card expand duration (currently
//                           300 ms — Tailwind duration-300 on grid-template-rows
//                           in BuildQuizzesQuestionCard.tsx) so adjacent cards
//                           have finished expanding before we measure the new
//                           card position in phase 2.
//
//   DROP_HIGHLIGHT_FADE_MS  how long the blue drop-target background fades out
//                           once the card expands. Must match the 750 ms inline
//                           style transition on the Card in BuildQuizzesQuestionCard.tsx.
//
//   SCROLL_END_FALLBACK_MS  safety timeout that fires the next step if the
//                           browser never emits a scrollend event (e.g. the
//                           target was already in the viewport). Applied
//                           independently to each of the two scroll phases.
//                           1200 ms works well in practice.
const SCROLL_TRIGGER_DELAY_MS = 450; // card expand (300 ms) + buffer
const DROP_HIGHLIGHT_FADE_MS = 750; // must match 750ms inline style transition in BuildQuizzesQuestionCard.tsx
const SCROLL_END_FALLBACK_MS = 1200;

const createQuestionReferenceId = () => {
  const generatedId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return {
    en: generatedId,
    es: generatedId,
  };
};

type QuizQuestionsSource = QuizEditorQuestions | QuizDetail['questions'];
type QuizQuestionSource = QuizQuestionsSource[number];

const DEFAULT_QUIZ = {
  ownerId: '',
  name: {
    en: '',
  },
  passingGrade: undefined,
  questions: [
    {
      ...Constants.DEFAULT_MULTIPLE_CHOICE_QUESTION,
      referenceId: createQuestionReferenceId(),
    },
  ],
  formId: '',
  quizId: '',
} satisfies EditableQuizDetail;

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0;

const getQuestionIdentifier = (question: QuizQuestionSource) => {
  const pickFrom = (
    record: Record<string, string> | null | undefined,
  ): string | undefined => {
    const en = record?.en;
    const es = record?.es;

    if (isNonEmptyString(en)) return en;
    if (isNonEmptyString(es)) return es;
    return Object.values(record ?? {}).find(isNonEmptyString);
  };

  return pickFrom(question.referenceId) ?? pickFrom(question.id);
};

const normalizeQuizQuestions = (
  questions: QuizQuestionsSource,
): QuizEditorQuestions => {
  const editorQuestions = questions as QuizEditorQuestions;
  return editorQuestions.map(q =>
    getQuestionIdentifier(q)
      ? q
      : { ...q, referenceId: createQuestionReferenceId() },
  );
};

const quizStateFromDraft = (draft: QuizDraft): EditableQuizDetail => {
  const {
    existingQuizId: _existingQuizId,
    savedAt: _savedAt,
    pendingSave: _pendingSave,
    buildStartedAt: _buildStartedAt,
    ...quiz
  } = draft;

  return {
    ...quiz,
    questions: normalizeQuizQuestions(quiz.questions),
  };
};

const omitQuestionIdentifiers = (
  questions: QuizQuestionsSource,
): QuizEditorQuestions => {
  const editorQuestions = questions as QuizEditorQuestions;
  return editorQuestions.map(question => {
    const { referenceId: _referenceId, id: _id, ...rest } = question;
    return {
      ...rest,
      answers: rest.answers.map(answer => {
        const {
          referenceId: _answerReferenceId,
          id: _answerId,
          ...answerRest
        } = answer;
        return answerRest;
      }),
    };
  });
};

const toComparableQuiz = (value: EditableQuizDetail | QuizDetail) => {
  const source = quizDetailToUpdateBody(value);
  return {
    ...source,
    passingGrade:
      typeof source.passingGrade === 'string'
        ? Number(source.passingGrade)
        : source.passingGrade,
    // Do not mint identifiers here. Hydrate assigns stable ids for the editor;
    // regenerating them on GET during compare makes a clean quiz look dirty.
    questions: omitQuestionIdentifiers(source.questions),
  };
};

const BuildQuizzesView = () => {
  const [storedQuiz, setStoredQuiz] = useAtom(quizDraftAtom);
  const { quizId } = useParams<{ quizId: string }>();
  const { t } = useTranslation();
  const { data: user } = useAuth();
  const isLicenseeUser = user?.isLicensee;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const userIsStaff = user?.isStaff;
  const userId = user?.userId;
  const breakpoints = useBreakpoints();
  const isSmAndDown = useMediaQuery(breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as BuildQuizzesLocationState | null;

  const track = useAmplitudeTrack();

  const userLanguage = user?.language ?? 'en';
  const renameLanguageKey =
    user?.language === '' || user?.language == null
      ? Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE
      : userLanguage;

  const [isDragging, setIsDragging] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [collapsedCardId, setCollapsedCardId] = useState<string | null>(null);
  const [droppedCardId, setDroppedCardId] = useState<string | null>(null);

  const questionIdsRef = useRef<string[]>([]);
  const dragTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const scrollEndListenerRef = useRef<(() => void) | null>(null);
  const dragSourceOriginalTopRef = useRef<number | null>(null);

  useEffect(() => {
    const cleanup = () => {
      dragTimeoutsRef.current.forEach(clearTimeout);
      if (scrollEndListenerRef.current) {
        window.removeEventListener('scrollend', scrollEndListenerRef.current);
      }
    };
    return cleanup;
  }, []);

  const [validationMode, setValidationMode] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);
  const [quiz, setQuiz] = useState<EditableQuizDetail>(() => ({
    ...DEFAULT_QUIZ,
    questions: normalizeQuizQuestions(DEFAULT_QUIZ.questions),
  }));
  const [showSaveBeforeRenaming, setShowSaveBeforeRenaming] = useState(false);
  const [showSaveBeforeTranslating, setShowSaveBeforeTranslating] =
    useState(false);
  const [showAddToPlanPopup, setShowAddToPlanPopup] = useState(false);
  const [isCreatingQuiz, setIsCreatingQuiz] = useState(false);
  const [showConfirmClosePopUp, setShowConfirmClosePopUp] = useState(false);
  const [showDeleteQuizPopup, setShowDeleteQuizPopup] = useState(false);
  const [showRenameQuizPopup, setShowRenameQuizPopup] = useState(false);
  const [showManageTranslationsPopup, setShowManageTranslationsPopup] =
    useState(false);

  // Track which quiz context we've initialized to avoid infinite loop with draft saving
  // and to re-initialize when navigating between edit URLs without remounting.
  const initializedContextRef = useRef<string | null>(null);
  const buildStartedAtRef = useRef<number | null>(null);
  const serverQuestionIdentifiersRef = useRef(emptyServerQuestionIdentifiers());
  // Incremented on successful persist so an in-flight 500ms saveDraft cannot
  // rewrite the localStorage draft after we clear it (PATHWAY-3939).
  const draftSaveGenerationRef = useRef(0);
  const skipNextDraftSaveRef = useRef(false);

  const discardStoredQuizDraft = (quizIdToClear?: string) => {
    draftSaveGenerationRef.current += 1;
    setStoredQuiz(current =>
      quizIdToClear ? clearMatchingQuizDraft(current, quizIdToClear) : null,
    );
  };

  // Shared create+edit route uses sentinel `quizId=new` (not a real API id).
  const isEditMode = !!quizId && quizId !== CREATE_ROUTE_QUIZ_ID;
  const quizContextKey = isEditMode ? `edit:${quizId}` : 'new';

  const hydrateQuizFromServer = (next: EditableQuizDetail | QuizDetail) => {
    skipNextDraftSaveRef.current = true;
    setQuiz({
      ...quizDetailToUpdateBody(next),
      questions: normalizeQuizQuestions(next.questions),
    });
    initializedContextRef.current = quizContextKey;
  };

  // Keep questionIdsRef in sync with quiz.questions length
  while (questionIdsRef.current.length < quiz.questions.length) {
    questionIdsRef.current.push(crypto.randomUUID());
  }

  const handleDeleteQuestion = (questionIndex: number) => {
    questionIdsRef.current.splice(questionIndex, 1);
  };

  const clearDragTimers = () => {
    dragTimeoutsRef.current.forEach(clearTimeout);
    dragTimeoutsRef.current = [];
    if (scrollEndListenerRef.current) {
      window.removeEventListener('scrollend', scrollEndListenerRef.current);
      scrollEndListenerRef.current = null;
    }
  };

  const onDragStart = (event: DragStartEvent) => {
    const { source } = event.operation;
    if (!isSortable(source)) return;
    clearDragTimers();
    // Pre-apply the blue highlight so the card is already styled when it
    // expands after drop — the card is collapsed/hidden during the drag so
    // this is invisible until expansion.
    setDroppedCardId(questionIdsRef.current[source.index] ?? null);
    setCollapsedCardId(null);
    setIsDragging(true);
    setDraggingIndex(source.index);
    // Capture the card's absolute document position before drag transforms
    // are applied — used in onDragEnd to scroll back to the original slot.
    const cardId = questionIdsRef.current[source.index];
    const el = cardId
      ? document.querySelector(`[data-question-id="${cardId}"]`)
      : null;
    dragSourceOriginalTopRef.current = el
      ? el.getBoundingClientRect().top + window.scrollY - 100
      : null;
  };

  const onDragEnd = (event: DragEndEvent) => {
    setIsDragging(false);
    setDraggingIndex(null);
    clearDragTimers();
    if (event.canceled) {
      setDroppedCardId(null);
      return;
    }
    const { source } = event.operation;
    if (!isSortable(source)) {
      setDroppedCardId(null);
      return;
    }
    const { initialIndex, index } = source;
    if (initialIndex === index) {
      setDroppedCardId(null);
      return;
    }
    setQuiz(prev => {
      const questions = [...prev.questions];
      const [removed] = questions.splice(initialIndex, 1);
      if (removed !== undefined) {
        questions.splice(index, 0, removed);
      }
      return { ...prev, questions };
    });
    const ids = [...questionIdsRef.current];
    const [removedId] = ids.splice(initialIndex, 1);
    if (removedId !== undefined) {
      ids.splice(index, 0, removedId);
      // Keep drop target collapsed while adjacent cards expand
      setCollapsedCardId(removedId);
      const originalScrollTarget = dragSourceOriginalTopRef.current;
      dragSourceOriginalTopRef.current = null;
      // After adjacent cards have expanded, run the two-phase scroll:
      // Phase 1 — instantly jump to where the card was picked up from.
      // Phase 2 — smooth scroll to its new position, then expand.
      dragTimeoutsRef.current.push(
        setTimeout(() => {
          const expand = () => {
            setCollapsedCardId(null);
            // droppedCardId was set at drag start so the card is already blue
            // as it expands; just schedule the clear.
            dragTimeoutsRef.current.push(
              setTimeout(() => setDroppedCardId(null), DROP_HIGHLIGHT_FADE_MS),
            );
          };
          const scrollToNewThenExpand = () => {
            const el = document.querySelector(
              `[data-question-id="${removedId}"]`,
            );
            if (el) {
              const newTop =
                el.getBoundingClientRect().top + window.scrollY - 100;
              window.scrollTo({ top: newTop, behavior: 'smooth' });
            }
            const fallback = setTimeout(() => {
              if (scrollEndListenerRef.current) {
                window.removeEventListener(
                  'scrollend',
                  scrollEndListenerRef.current,
                );
                scrollEndListenerRef.current = null;
              }
              expand();
            }, SCROLL_END_FALLBACK_MS);
            dragTimeoutsRef.current.push(fallback);
            const onScrollEnd = () => {
              clearTimeout(fallback);
              scrollEndListenerRef.current = null;
              expand();
            };
            scrollEndListenerRef.current = onScrollEnd;
            window.addEventListener('scrollend', onScrollEnd, { once: true });
          };
          if (originalScrollTarget !== null) {
            window.scrollTo({ top: originalScrollTarget, behavior: 'instant' });
          }
          scrollToNewThenExpand();
        }, SCROLL_TRIGGER_DELAY_MS),
      );
    } else {
      setDroppedCardId(null);
    }
    questionIdsRef.current = ids;
  };

  const invalidateQuizzes = async (id?: string) => {
    const invalidations = [
      queryClient.invalidateQueries({
        queryKey: getQuizzesQueryKey(),
        refetchType: 'all',
      }),
    ];

    if (id) {
      invalidations.push(
        queryClient.refetchQueries({
          queryKey: getQuizDetailsQueryKey({
            path: {
              quizId: id,
            },
          }),
        }),
      );
    }

    await Promise.all(invalidations);
  };

  const syncRenamedQuizName = (
    quizIdToSync: string,
    updatedName: EditableQuizDetail['name'],
  ) => {
    skipNextDraftSaveRef.current = true;
    setQuiz(prev => ({ ...prev, name: updatedName }));
    patchQuizDetailsQueryCache(quizIdToSync, { name: updatedName });
    discardStoredQuizDraft(quizIdToSync);
  };

  const addQuiz = async (
    body: QuizCreateRequest,
    addToPlan: boolean,
    onSuccess?: () => void,
  ) => {
    try {
      // Mark that a save is in progress SYNCHRONOUSLY (must survive Okta redirect)
      // Using sync localStorage write because Jotai's async update may not complete before redirect
      setPendingSaveFlagSync(true);

      const { data, error } = await createQuizRequest({ body });

      // Check for API error (the client doesn't throw by default)
      // NOTE: Don't clear pendingSave on error - an auth error (500) may trigger
      // an Okta redirect, and we want the flag to survive so the user is warned
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        return;
      }

      // Clear pendingSave flag only on success
      setPendingSaveFlagSync(false);
      discardStoredQuizDraft();
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${itemByLanguage(quiz.name)} ${t(
            'TrainingPlans.toastMessage.created',
          )}`}
        </ToastMessageBlock>
      ));
      track(AmplitudeProductEvent.QUIZ_CREATED, {
        quiz_id: data.id,
        ...buildQuizWallClockDurationProps(buildStartedAtRef.current),
      });
      buildStartedAtRef.current = null;
      await invalidateQuizzes();
      onSuccess?.();

      if (addToPlan) {
        navigate({
          pathname: routes.plans.managedPlans,
        });
      } else {
        navigate(routes.plans.managedQuizzes);
      }
    } catch (error) {
      // NOTE: Don't clear pendingSave on error - the error might precede an Okta redirect
      if (isApiError(error as CustomError | Error)) {
        datadogRum.addError(error);
        return <GenericError />;
      }
      datadogRum.addError(error);
    }
  };

  const updateQuiz = async (
    body: EditableQuizDetail,
    onSuccess?: () => void,
  ) => {
    try {
      if (!body.quizId)
        throw new Error('Quiz ID is required for updating a quiz.');

      const updateBody = toQuizUpdateRequest(body);

      // Mark that a save is in progress SYNCHRONOUSLY (must survive Okta redirect)
      // Using sync localStorage write because Jotai's async update may not complete before redirect
      setPendingSaveFlagSync(true);

      const { error } = await updateQuizRequest({
        path: { quizId: body.quizId },
        body: updateBody,
      });

      // Check for API error (the client doesn't throw by default)
      // NOTE: Don't clear pendingSave on error - an auth error (500) may trigger
      // an Okta redirect, and we want the flag to survive so the user is warned
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        return;
      }

      // Clear pendingSave flag only on success
      setPendingSaveFlagSync(false);
      discardStoredQuizDraft(body.quizId);
      await invalidateQuizzes(body.quizId);
      const freshQuiz = queryClient.getQueryData<QuizDetail>(
        getQuizDetailsQueryKey({
          path: { quizId: body.quizId },
        }),
      );
      if (freshQuiz) {
        hydrateQuizFromServer(freshQuiz);
      }
      onSuccess?.();
    } catch (error) {
      // NOTE: Don't clear pendingSave on error - the error might precede an Okta redirect
      datadogRum.addError(error);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorUpdating')}
        </ToastMessageBlock>
      ));
    }
  };

  const deleteQuiz = async (id: string, onSuccess?: () => void) => {
    try {
      const { error } = await deleteQuizRequest({ path: { quizId: id } });

      // Check for API error (the client doesn't throw by default)
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorDeleting')}
          </ToastMessageBlock>
        ));
        return;
      }

      await invalidateQuizzes();
      onSuccess?.();
    } catch (error) {
      datadogRum.addError(error);
    }
  };

  const {
    data: quizData,
    isFetching: isFetchingQuiz,
    isPending: isPendingQuiz,
  } = useQuery({
    ...getQuizDetailsOptions({
      path: {
        quizId: quizId ?? '',
      },
    }),
    enabled: isEditMode,
  });

  const isFetching = isPendingQuiz && isEditMode;

  // Derived state: calculate isEdited directly instead of using useEffect
  // React Compiler will auto-memoize this computation
  const comparableQuiz = toComparableQuiz(quiz);
  const isEdited = quizData
    ? !isEqual(comparableQuiz, toComparableQuiz(quizData))
    : false;
  const isQuizInitialized = initializedContextRef.current === quizContextKey;
  const hasRestoredEditDraft = shouldRestoreQuizDraft(storedQuiz, {
    isEditMode,
    quizId,
  });
  // New quizzes are unsaved work. In edit mode, dirty vs GET — or a restored
  // draft before GET has arrived, when we have no baseline yet.
  const hasUnsavedQuizWork =
    isQuizInitialized &&
    (isEditMode ? (quizData ? isEdited : hasRestoredEditDraft) : true);

  const prepareQuizOrNavigate = useEffectEvent(() => {
    const contextKey = quizContextKey;
    if (initializedContextRef.current === contextKey) {
      return;
    }

    // Scenario 1: Restore a matching draft (PATHWAY-2933: refresh / Okta /
    // return to editor from the list). Successful PUT clears the draft.
    const draftToRestore = shouldRestoreQuizDraft(storedQuiz, {
      isEditMode,
      quizId,
    })
      ? storedQuiz
      : null;

    if (draftToRestore) {
      // Restore from draft (handles browser refresh and Okta redirect)
      setQuiz(quizStateFromDraft(draftToRestore));
      if (!isEditMode) {
        buildStartedAtRef.current = draftToRestore.buildStartedAt ?? null;
      }
      if (quizData?.questions) {
        serverQuestionIdentifiersRef.current = collectServerQuestionIdentifiers(
          quizData.questions,
        );
      }
      initializedContextRef.current = contextKey;

      // If a save was in progress when redirected, warn the user
      if (draftToRestore.pendingSave) {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        // Clear the flag in both localStorage (sync) and Jotai state
        setPendingSaveFlagSync(false);
        setStoredQuiz({ ...draftToRestore, pendingSave: false });
      }
    } else if (
      !isEditMode &&
      locationState?.quizName &&
      (locationState.operator?.id || userIsStaff)
    ) {
      // Scenario 2: Fresh navigation with location.state (new quiz from popup)
      const { quizName, operator } = locationState;
      setQuiz(prevState => ({
        ...prevState,
        ownerId: operator?.id ?? userId ?? '',
        name: { en: quizName },
      }));
      buildStartedAtRef.current = locationState.buildStartedAt ?? Date.now();
      initializedContextRef.current = contextKey;
    } else if (isEditMode) {
      // Scenario 3: Edit mode - load from API
      if (quizData) {
        setQuiz({
          ...quizDetailToUpdateBody(quizData),
          questions: normalizeQuizQuestions(quizData.questions),
        });
        initializedContextRef.current = contextKey;
      } else if (!isFetchingQuiz) {
        navigate(routes.plans.managedQuizzes);
      }
    } else {
      // Scenario 4: No valid context - redirect back
      navigate(routes.plans.managedQuizzes);
    }
  });

  useEffect(() => {
    const contextKey = quizContextKey;

    if (
      initializedContextRef.current !== null &&
      initializedContextRef.current !== contextKey
    ) {
      initializedContextRef.current = null;
      buildStartedAtRef.current = null;
      serverQuestionIdentifiersRef.current = emptyServerQuestionIdentifiers();
      questionIdsRef.current = [];
      setQuiz({
        ...DEFAULT_QUIZ,
        questions: normalizeQuizQuestions(DEFAULT_QUIZ.questions),
      });
    }
  }, [quizId, isEditMode, quizContextKey]);

  useEffect(() => {
    prepareQuizOrNavigate();
  }, [quizData, quizId]);

  useEffect(() => {
    if (isEditMode && quizData?.questions && quizData.quizId === quizId) {
      serverQuestionIdentifiersRef.current = collectServerQuestionIdentifiers(
        quizData.questions,
      );
    }
  }, [quizData, quizId, isEditMode]);

  const getServerQuestionIdentifiersForSave = () => {
    const sourceQuestions =
      quiz.quizId && quiz.quizId === quizId ? quizData?.questions : undefined;

    return resolveServerQuestionIdentifiers(
      serverQuestionIdentifiersRef.current,
      sourceQuestions,
    );
  };

  const saveDraft = useEffectEvent(() => {
    // Skip saving until we've finished initializing the quiz state
    // This prevents an infinite loop: quiz changes -> save to storedQuiz -> storedQuiz changes -> restore quiz
    if (initializedContextRef.current !== quizContextKey) {
      return;
    }

    if (isEditMode && !quizData) {
      return;
    }

    if (isEditMode && !isEdited) {
      // Matches GET — drop a leftover dirty draft so revert + leave + re-open
      // cannot restore discarded edits.
      discardStoredQuizDraft(quizId);
      return;
    }

    // Save draft for BOTH new quizzes and edit sessions
    // This ensures state survives browser refresh and Okta redirect
    // NOTE: Read pendingSave directly from localStorage to avoid race conditions with Jotai's async state
    setStoredQuiz({
      ...quiz,
      questions: [...quiz.questions],
      existingQuizId: isEditMode ? quizId : null,
      savedAt: Date.now(),
      ...(isEditMode
        ? {}
        : { buildStartedAt: buildStartedAtRef.current ?? undefined }),
      pendingSave: getPendingSaveFlagSync(),
    });
  });

  // Trailing debounce: wait 500ms after the last quiz change before writing
  // localStorage. Cleanup cancels the timer when `quiz` changes again.
  // Successful PUT does not always change `quiz` (full save navigates away), so
  // the generation check drops an in-flight write after we clear the draft.
  // Stay-on-page PUTs hydrate from GET and skip the next scheduled save so the
  // server copy is not rewritten as a draft.
  useEffect(() => {
    if (skipNextDraftSaveRef.current) {
      skipNextDraftSaveRef.current = false;
      return;
    }
    const generation = draftSaveGenerationRef.current;
    const timeoutId = setTimeout(() => {
      if (generation !== draftSaveGenerationRef.current) {
        return;
      }
      saveDraft();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [quiz]);

  const addQuestion = () => {
    const newQuestion = {
      ...Constants.DEFAULT_MULTIPLE_CHOICE_QUESTION,
      referenceId: createQuestionReferenceId(),
    };

    setValidationMode(false);
    handlePassingGrade();
    setQuiz(prevState => ({
      ...prevState,
      questions: [...prevState.questions, newQuestion],
    }));
  };

  const moveQuestionUp = (questionIndex: number) => {
    if (questionIndex <= 0 || questionIndex >= quiz.questions.length) return;

    const ids = questionIdsRef.current;
    const prevId = ids[questionIndex - 1];
    const currId = ids[questionIndex];
    if (prevId !== undefined && currId !== undefined) {
      ids[questionIndex - 1] = currId;
      ids[questionIndex] = prevId;
    }

    setQuiz(prevState => {
      const newQuestions = [...prevState.questions];
      const prevQuestion = newQuestions[questionIndex - 1];
      const currentQuestion = newQuestions[questionIndex];
      if (prevQuestion !== undefined && currentQuestion !== undefined) {
        newQuestions[questionIndex - 1] = currentQuestion;
        newQuestions[questionIndex] = prevQuestion;
      }
      return {
        ...prevState,
        questions: newQuestions,
      };
    });
  };

  const moveQuestionDown = (questionIndex: number) => {
    if (questionIndex < 0 || questionIndex >= quiz.questions.length - 1) return;

    const ids = questionIdsRef.current;
    const currId = ids[questionIndex];
    const nextId = ids[questionIndex + 1];
    if (currId !== undefined && nextId !== undefined) {
      ids[questionIndex] = nextId;
      ids[questionIndex + 1] = currId;
    }

    setQuiz(prevState => {
      const newQuestions = [...prevState.questions];
      const currentQuestion = newQuestions[questionIndex];
      const nextQuestion = newQuestions[questionIndex + 1];
      if (currentQuestion !== undefined && nextQuestion !== undefined) {
        newQuestions[questionIndex] = nextQuestion;
        newQuestions[questionIndex + 1] = currentQuestion;
      }
      return {
        ...prevState,
        questions: newQuestions,
      };
    });
  };

  const validateQuiz = () => {
    if (quiz.passingGrade === undefined) return false;

    return quiz.questions.every(
      question =>
        validateTextField(question.question[userLanguage] ?? '') &&
        question.answers.some(option => option.correct === true) &&
        question.answers.every(answer =>
          validateTextField(answer.answer[userLanguage] ?? ''),
        ),
    );
  };

  const onConfirmClose = () => {
    discardStoredQuizDraft();
    setShowConfirmClosePopUp(false);
    navigate({
      pathname: routes.plans.managedQuizzes,
    });
  };

  const onSaveQuiz = () => {
    setValidationMode(true);
    const isValid = validateQuiz();
    if (!isValid) {
      setShowValidationError(true);
      return;
    } else if (isEditMode) {
      editQuiz();
    } else {
      setShowAddToPlanPopup(true);
    }
  };

  const editQuiz = () => {
    const isValid = validateQuiz();
    if (!isValid) {
      setShowValidationError(true);
      return;
    }
    const parsedQuestions = prepareQuestionsForSave(
      quiz.questions,
      getServerQuestionIdentifiersForSave(),
    );

    updateQuiz(
      quizDetailToUpdateBody(quiz, {
        questions: parsedQuestions,
        passingGrade: Number(quiz.passingGrade),
      }),
      () => {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {`${getNameFromLanguage(quiz.name)} ${t(
              'TrainingPlans.toastMessage.updated',
            )}`}
          </ToastMessageBlock>
        ));
        // fire amplitude event
        track(AmplitudeProductEvent.QUIZ_UPDATED, {
          quiz_id: quizId ?? 'empty',
        });
        navigate(routes.plans.managedQuizzes);
      },
    );
  };

  const createQuiz = (addToPlan: boolean) => {
    if (isCreatingQuiz) {
      return;
    }
    setIsCreatingQuiz(true);
    const parsedQuestions = prepareQuestionsForSave(
      quiz.questions,
      emptyServerQuestionIdentifiers(),
      { isCreate: true },
    );

    const { ownerId: operatorId, ...quizBody } = quiz;
    addQuiz(
      {
        ...quizBody,
        operatorId,
        location: isLicenseeUser ? activeLicenseeLocation?.number : undefined,
        questions: parsedQuestions,
        passingGrade: Number(quiz.passingGrade),
        v2: false,
      },
      addToPlan,
    ).finally(() => {
      setIsCreatingQuiz(false);
      setShowAddToPlanPopup(false);
    });
  };

  const onSaveTranslations = (updatedTranslations: ManageTranslationsState) => {
    setShowManageTranslationsPopup(false);
    const payload = updatedTranslations as EditableQuizDetail;

    updateQuiz(
      quizDetailToUpdateBody(payload, {
        questions: prepareQuestionsForSave(
          payload.questions,
          getServerQuestionIdentifiersForSave(),
        ),
      }),
      () => {
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id}>
            {`${itemByLanguage(updatedTranslations.name)} ${t(
              'TrainingPlans.translationToastText',
            )} ${
              userLanguage === Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE
                ? Constants.LANGUAGE_OPTIONS.SPANISH
                : Constants.LANGUAGE_OPTIONS.INGLES
            }`}
          </ToastMessageBlock>
        ));
      },
    );
  };

  const onRenameQuiz = async (newName: string): Promise<boolean> => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      return false;
    }

    const updatedName = {
      ...quiz.name,
      [renameLanguageKey]: trimmedName,
    };

    if (!quiz.quizId) {
      // Create flow seeds name.en only; keep en in sync until first save.
      updatedName[Constants.LANGUAGE.ENGLISH_LANGUAGE_CODE] = trimmedName;
      setQuiz(prev => ({ ...prev, name: updatedName }));
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${trimmedName} ${t('TrainingPlans.toastMessage.renamed')}`}
        </ToastMessageBlock>
      ));
      return true;
    }

    try {
      const { error } = await patchQuizName({
        path: { quizId: quiz.quizId },
        body: {
          name: updatedName,
        },
      });
      if (error) {
        datadogRum.addError(error);
        toast.custom(toastObj => (
          <ToastMessageBlock id={toastObj.id} severity="error">
            {t('TrainingPlans.toastMessage.errorUpdating')}
          </ToastMessageBlock>
        ));
        return false;
      }

      syncRenamedQuizName(quiz.quizId, updatedName);
      await invalidateQuizzes();
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>
          {`${trimmedName} ${t('TrainingPlans.toastMessage.renamed')}`}
        </ToastMessageBlock>
      ));
      return true;
    } catch (error) {
      datadogRum.addError(error);
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorUpdating')}
        </ToastMessageBlock>
      ));
      return false;
    }
  };

  const onDeleteQuiz = (id: string, quizName: string) => {
    deleteQuiz(id, () => {
      toast.custom(toastObj => (
        <ToastMessageBlock id={toastObj.id}>{`${quizName} ${t(
          'TrainingPlans.toastMessage.deleted',
        )}`}</ToastMessageBlock>
      ));
      navigate({
        pathname: routes.plans.managedQuizzes,
      });
    });
  };

  const handlePassingGrade = (value?: number) => {
    setQuiz(prevState => ({
      ...prevState,
      passingGrade: value,
    }));
  };

  const onShowRenameQuizPopup = () => {
    if (isEdited) {
      setShowSaveBeforeRenaming(true);
    } else {
      setShowRenameQuizPopup(true);
    }
  };

  const onDeleteQuizAndClosePopup = () => {
    onDeleteQuiz(quiz.quizId, itemByLanguage(quiz.name, ''));
    setShowDeleteQuizPopup(false);
  };

  const onRenameQuizAndClosePopup = async (inputValue: string) => {
    const renamed = await onRenameQuiz(inputValue);
    if (renamed) {
      setShowRenameQuizPopup(false);
    }
    return renamed;
  };

  const onShowDeleteQuizPopup = () => {
    setShowDeleteQuizPopup(true);
  };

  const onShowManageTranslationsPopup = () => {
    if (isEdited) {
      setShowSaveBeforeTranslating(true);
    } else {
      setShowManageTranslationsPopup(true);
    }
  };

  const quizTitle = itemByLanguage(quiz.name, '', renameLanguageKey);

  // Create entry is already limited to operators the user can manage quizzes for.
  // Editor state drops ownerId, so edit mode reads it from the GET detail.
  const canManageThisQuiz =
    !isEditMode ||
    (!!quizData?.ownerId &&
      (user?.locationsWithRoles.some(
        location =>
          location.permissions.canManageQuizzes &&
          location.ownerId === quizData.ownerId,
      ) ??
        false));

  return (
    <>
      <Header.Root>
        <Header.Back
          to={routes.plans.managedQuizzes}
          tx="TrainingPlans.tabManageQuizzes"
        />
        <Header.Title tx="Generic.editQuiz" />
      </Header.Root>
      <LoadingOverlay isOpen={isFetching} fixed />
      {!isFetching && (
        <div
          className={cn(
            'md:mt-header-height-with-content-spacing centered-content px-4 max-md:mt-6',
            !canManageThisQuiz && 'pointer-events-none cursor-not-allowed',
          )}
        >
          <div className="mb-6 flex items-center justify-between gap-4">
            <p className="text-variant-h3 truncate text-left">{quizTitle}</p>
            <PopoverMenuButton className="md:hidden">
              <PopoverMenuButtonItem
                dataTestId="ThreeDotMenuQuizPopupRename"
                icon={<IconEdit />}
                onClick={onShowRenameQuizPopup}
                text={t('TrainingPlans.manageThreeDotMenu.renameQuiz')}
              />
              <PopoverMenuButtonItem
                dataTestId="ThreeDotMenuManageTranslationsPopup"
                icon={<IconWorld />}
                onClick={onShowManageTranslationsPopup}
                text={t('Generic.editTranslations')}
              />
              <PopoverMenuButtonItem
                dataTestId="deleteQuizMenuItem"
                icon={<IconTrash />}
                isDestructive
                onClick={onShowDeleteQuizPopup}
                text={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
              />
            </PopoverMenuButton>
          </div>
          <div className="flex flex-col gap-6 md:flex-row">
            <div className="size-full">
              <DragDropProvider onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <div>
                  {quiz.questions.map((_question, questionIndex) => {
                    const cardId = questionIdsRef.current[questionIndex];
                    return (
                      <SortableQuestionCard
                        key={cardId}
                        handlePassingGrade={handlePassingGrade}
                        isCollapsed={cardId === collapsedCardId}
                        isDropTarget={cardId === droppedCardId}
                        isEditMode={isEditMode}
                        isAnyDragActive={isDragging}
                        moveQuestionDown={moveQuestionDown}
                        moveQuestionUp={moveQuestionUp}
                        onDeleteQuestion={() =>
                          handleDeleteQuestion(questionIndex)
                        }
                        questionId={cardId ?? ''}
                        questionIndex={questionIndex}
                        quiz={quiz}
                        setQuiz={setQuiz}
                        setValidationMode={setValidationMode}
                        totalQuestions={quiz.questions.length}
                        validationMode={validationMode}
                      />
                    );
                  })}
                </div>
                <DragOverlay>
                  {(() => {
                    const draggedQuestion =
                      draggingIndex !== null
                        ? quiz.questions[draggingIndex]
                        : undefined;
                    const draggedQuestionHasError =
                      validationMode &&
                      draggedQuestion !== undefined &&
                      (!validateTextField(
                        draggedQuestion.question[userLanguage] ?? '',
                      ) ||
                        !draggedQuestion.answers.some(
                          option => option.correct === true,
                        ) ||
                        draggedQuestion.answers.some(
                          a => !validateTextField(a.answer[userLanguage] ?? ''),
                        ));
                    return draggedQuestion !== undefined &&
                      draggingIndex !== null ? (
                      <CollapsedQuestionPreview
                        question={draggedQuestion}
                        questionIndex={draggingIndex}
                        isHighlighted
                        hasError={draggedQuestionHasError}
                      />
                    ) : null;
                  })()}
                </DragOverlay>
              </DragDropProvider>
              <div className="mt-4 mb-12 text-left">
                <Button
                  className="mb-4 pl-2!"
                  color="secondary"
                  onClick={addQuestion}
                  size="lg"
                  variant="text"
                >
                  <IconPlus />
                  {t('Button.addQuestion')}
                </Button>
              </div>
              <Card elevation={1} variant="outlined">
                <CardContent>
                  <div className="mt-2 mb-4 text-left [&_.cfa-dropdown]:max-w-[256px]">
                    <Dropdown
                      data-testid="QuizQuestionDropdown"
                      className="[&_button]:w-auto! [&_button]:min-w-[100px]!"
                      errorText={
                        !quiz.passingGrade && validationMode
                          ? t(
                              'TrainingPlans.buildQuizzes.errorSelectPassingGrade',
                            )
                          : ''
                      }
                      fullWidth
                      label={t(
                        'TrainingPlans.buildQuizzes.correctQuestionsThreshold',
                      )}
                      onChange={newVal => handlePassingGrade(Number(newVal))}
                      options={[
                        '0',
                        ...quiz.questions.map((_, index) =>
                          (index + 1).toString(),
                        ),
                      ]}
                      placeholder={t('Button.select')}
                      required
                      value={quiz.passingGrade?.toString()}
                    />
                  </div>
                </CardContent>
              </Card>
              <div className="mt-4 mb-12 flex flex-col justify-center md:mx-0 md:mt-8 md:flex-row">
                <Button
                  className="mt-3.5! min-w-[unset]! md:mx-2!"
                  color="secondary"
                  disabled={!hasUnsavedQuizWork}
                  onClick={() => {
                    setShowConfirmClosePopUp(true);
                  }}
                  size="md"
                  variant="outlined"
                >
                  {t('Button.cancel')}
                </Button>
                <Button
                  className="mt-3.5! min-w-[unset]! md:mx-2!"
                  color="secondary"
                  data-testid="SaveQuiz"
                  disabled={!hasUnsavedQuizWork}
                  onClick={onSaveQuiz}
                  size="md"
                  variant="filled"
                >
                  {t('Button.saveQuiz')}
                </Button>
              </div>
            </div>
            <ConfirmationModal
              bodyText={t('Generic.cancelConfirmation')}
              headerText={t('Generic.unsavedChanges')}
              isOpen={showConfirmClosePopUp}
              onClose={() => setShowConfirmClosePopUp(false)}
              primaryButtonHandler={onConfirmClose}
              primaryButtonText={t('Button.discardChanges')}
              primaryButtonVariant="destructive"
              secondaryButtonHandler={() => setShowConfirmClosePopUp(false)}
              secondaryButtonText={t('Button.continueEditing')}
            />
            <ConfirmationModal
              bodyText={t('TrainingPlans.buildQuizzes.addToPlan')}
              headerText={t('TrainingPlans.buildQuizzes.addToPlanHeader')}
              isDisabled={isCreatingQuiz}
              isLoading={isCreatingQuiz}
              isOpen={showAddToPlanPopup}
              onClose={() => setShowAddToPlanPopup(false)}
              primaryButtonHandler={() => createQuiz(true)}
              primaryButtonText={t('Button.choosePlan')}
              secondaryButtonHandler={() => createQuiz(false)}
              secondaryButtonText={t('Button.notNow')}
            />
            <ValidationErrorPopup
              setShowValidationError={setShowValidationError}
              showValidationError={showValidationError}
            />
            <DeleteQuizPopup
              onDeleteQuizAndClosePopup={onDeleteQuizAndClosePopup}
              quizName={getNameFromLanguage(quiz.name)}
              setShowDeleteQuizPopup={setShowDeleteQuizPopup}
              showDeleteQuizPopup={showDeleteQuizPopup}
            />
            <RenameQuizPopup
              onRenameQuizAndClosePopup={onRenameQuizAndClosePopup}
              quizDetails={quiz}
              setShowRenameQuizPopup={setShowRenameQuizPopup}
              showRenameQuizPopup={showRenameQuizPopup}
            />
            <ManageTranslationsModal
              isOpen={showManageTranslationsPopup}
              onClose={() => setShowManageTranslationsPopup(false)}
              onSave={onSaveTranslations}
              translatableObject={quiz}
            />

            <ConfirmationModal
              bodyText={t('TrainingPlans.buildQuizzes.saveBeforeRenamingQuiz')}
              headerText={t('Generic.unsavedChanges')}
              isOpen={showSaveBeforeRenaming}
              onClose={() => setShowSaveBeforeRenaming(false)}
              primaryButtonHandler={() => setShowSaveBeforeRenaming(false)}
              primaryButtonText={t('Button.goBack')}
            />
            <ConfirmationModal
              bodyText={t(
                'TrainingPlans.buildQuizzes.saveBeforeTranslatingQuiz',
              )}
              headerText={t('Generic.unsavedChanges')}
              isOpen={showSaveBeforeTranslating}
              onClose={() => setShowSaveBeforeTranslating(false)}
              primaryButtonHandler={() => setShowSaveBeforeTranslating(false)}
              primaryButtonText={t('Button.goBack')}
            />
            {!isSmAndDown && isEditMode && (
              <StickyMenuCard
                header={t('TrainingPlans.buildQuizzes.manageQuiz')}
                className="md:top-[calc(var(--header-height)+16px)]!"
              >
                <StickyMenuButton
                  dataTestId="ThreeDotMenuQuizPopupRename"
                  icon={<IconEdit />}
                  onClick={onShowRenameQuizPopup}
                  text={t('TrainingPlans.manageThreeDotMenu.renameQuiz')}
                />
                <StickyMenuButton
                  dataTestId="ThreeDotMenuManageTranslationsPopup"
                  icon={<IconWorld />}
                  onClick={onShowManageTranslationsPopup}
                  text={t('Generic.editTranslations')}
                />
                <StickyMenuButton
                  dataTestId="deleteQuizMenuItem"
                  error
                  icon={<IconTrash />}
                  onClick={onShowDeleteQuizPopup}
                  text={t('TrainingPlans.manageThreeDotMenu.deleteQuiz')}
                />
              </StickyMenuCard>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const BuildQuizzesViewWithRoles = withRoles(BuildQuizzesView, 'quizzes.create');

export default BuildQuizzesViewWithRoles;
